import { PROVIDER_LABELS, SignInProviderId, linkProviderToUser, unlinkProviderFromUser } from './auth-link-errors';
import { AngularFirestore, AngularFirestoreDocument } from '@newgenus/fire-compat/firestore';
import { Particle, User, firestoreTimestampNow } from '@newgenus/common';
import { AngularFireAnalytics } from '@newgenus/fire-compat/analytics';
import { AngularFireAuth } from '@newgenus/fire-compat/auth';
import { switchMap, Observable, filter, map, shareReplay } from 'rxjs';
import { Injectable, NgZone } from '@angular/core';
import { Plan } from '@newgenus/common';
import { ApiService } from './api.service';
import firebase from 'firebase/compat/app';
import { Capacitor } from '@capacitor/core';
import { Router } from '@angular/router';
import { LastRouteService } from './last-route.service';
import { PresenceService } from './presence.service';
import { buildFirstTimeSetupData, decideFirstTimeSetup, ServerUserRead } from './auth-setup-data';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    public user$: Observable<User>;
    public user: any;
    private userToken: string | null | undefined;
    public redirectUrl!: string;
    public isLoggedIn!: boolean;
    public setupDone!: boolean;
    private oneInFlight = 0;
    private oauthedUser!: firebase.User;
    public isAdmin!: boolean;
    /**
     * Observable twin of `isAdmin` (B4): `isAdmin` is only ever SET during the interactive
     * sign-in flows (processOAuthResult/signInRegular), so a page refresh restores auth state
     * without it — an admin who reloads silently loses Delete/Access-tab/comment-admin powers
     * anywhere that read the snapshot field. Derived from the ID token's `admin` claim on every
     * token (re)fetch, which fires on refresh too, OR the legacy field (covers the brief window
     * before the first idTokenResult emission on a fresh interactive sign-in).
     */
    public isAdmin$!: Observable<boolean>;
    private registration!: boolean;

    constructor(
        public afAuth: AngularFireAuth,
        private afs: AngularFirestore,
        private apiService: ApiService,
        public ngZone: NgZone, // private particleService: ParticleService,
        private router: Router,
        private analytics: AngularFireAnalytics,
        private lastRouteService: LastRouteService,
        private presenceService: PresenceService
    ) {
        // Force durable LOCAL auth persistence (IndexedDB/localStorage). In the
        // Capacitor native WebView the default can silently fall back to in-memory,
        // logging the user out on every cold start; LOCAL keeps the session alive
        // across app restarts. No-op-safe on web (LOCAL is already the default there).
        this.afAuth
            .setPersistence(firebase.auth.Auth.Persistence.LOCAL)
            .catch((err) => console.warn('[AUTH] setPersistence(LOCAL) failed', err));

        this.isAdmin$ = this.afAuth.idTokenResult.pipe(
            map((idTokenResult) => idTokenResult?.claims?.['admin'] === true || !!this.isAdmin),
            shareReplay({ bufferSize: 1, refCount: false })
        );

        // Get auth data, then get firestore user document || null
        this.user$ = this.afAuth.authState.pipe(
            filter((user) => user !== null && user !== undefined),
            map((user: firebase.User | null) => user as firebase.User), // if the filter above was recognized, this map wouldn't be neeed.
            switchMap((user) => {
                this.user = user;
                this.isLoggedIn = true;

                return afs.doc<User>(`users/${user.uid}`).valueChanges();
            }),
            switchMap((user: User | undefined) => {
                if (user === null || user === undefined) {
                    // Auth session exists but Firestore user document is gone (user was deleted).
                    // Force sign out and redirect to login.
                    console.warn(
                        '[AUTH] User document missing in Firestore — session invalidated. Redirecting to login.'
                    );
                    this.handleInvalidSession();
                    return []; // complete the observable, no emission
                }
                return [user];
            }),
            // Cache the last good user emission so re-subscriptions (e.g. AuthGuard
            // re-running check() on every navigation) replay it instead of
            // re-racing the cold authState → valueChanges chain, which could
            // transiently read back as undefined and reject the guard.
            shareReplay({ bufferSize: 1, refCount: false })
        );

        this.afAuth.onAuthStateChanged((user) => {
            if (user) {
                firebase
                    .auth()
                    .currentUser?.getIdToken()
                    .then((idToken) => {
                        this.userToken = idToken;
                    })
                    .catch((err) => {
                        // Token refresh failed — user account was likely deleted server-side.
                        console.error(
                            '[AUTH] Token refresh failed — user account may have been deleted:',
                            err?.message || err
                        );
                        this.handleInvalidSession();
                    });
            } else {
                // Auth state is null. If the user was previously logged in OR the URL
                // indicates they were on a protected route, redirect to login.
                const onProtectedRoute = window.location.pathname.startsWith('/app');
                if (this.isLoggedIn || onProtectedRoute) {
                    console.warn('[AUTH] Auth state returned null while on protected route — redirecting to login.');
                    this.handleInvalidSession();
                }
            }
        });
    }

    public isInstantiated() {
        return new Promise<void>((resolve, reject) => {
            let isInstantiated = false;

            const subscription = this.user$.subscribe((user) => {
                if (user && user.preferences) {
                    isInstantiated = true;
                    resolve();
                } else if (user) {
                    // console.log('auth service > isInstantiated > not fully fletched user:', user);
                }
            });

            setTimeout(() => {
                if (!isInstantiated) {
                    subscription.unsubscribe();
                    reject();
                }
            }, 10000);
        });
    }

    async getUserToken() {
        if (this.userToken !== null && this.userToken !== undefined) {
            return this.userToken;
        } else {
            return await firebase
                .auth()
                .currentUser?.getIdToken()
                .then((idToken) => {
                    this.userToken = idToken;
                    return idToken;
                });
        }
    }

    // #region -------- Login/Sign-up to firebase   -----------------------------------

    public googleLogin(): Promise<void> {
        // Google blocks OAuth inside app WebViews, so popup/redirect both fail on
        // native. On-device use the native Google sign-in plugin instead.
        if (Capacitor.isNativePlatform()) {
            return this.nativeGoogleLogin();
        }
        const provider = new firebase.auth.GoogleAuthProvider();
        // Force account selection instead of auto-login
        provider.setCustomParameters({
            prompt: 'select_account',
        });
        return this.oAuthLogin(provider, 'google');
    }

    /**
     * Native (Capacitor) Google sign-in. Uses @capacitor-firebase/authentication
     * (native Google SDK / Custom Tabs) to obtain a credential, then signs into
     * the @angular/fire JS SDK with it — `skipNativeAuth` (capacitor.config) keeps
     * the JS layer the single source of truth (authState + LOCAL persistence). The
     * plugin is dynamically imported so its native code never loads on web.
     */
    private async nativeGoogleLogin(): Promise<void> {
        const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
        const result = await FirebaseAuthentication.signInWithGoogle();
        const idToken = result.credential?.idToken;
        if (!idToken) {
            throw new Error('Native Google sign-in returned no idToken');
        }
        const credential = firebase.auth.GoogleAuthProvider.credential(idToken, result.credential?.accessToken);
        const cred = await this.afAuth.signInWithCredential(credential);
        await this.processOAuthResult({ user: cred.user, credential: null }, 'google');
    }

    public microsoftLogin(): Promise<void> {
        const provider = new firebase.auth.OAuthProvider('microsoft.com');
        // Force account selection instead of auto-login
        provider.setCustomParameters({
            prompt: 'select_account',
        });
        return this.oAuthLogin(provider, 'microsoft');
    }

    private oAuthLogin(provider: any, oauthProvider: string) {
        return this.afAuth.signInWithPopup(provider).then(async (result: any) => {
            await this.processOAuthResult(result, oauthProvider);
        });
    }

    /**
     * Process a user that is already authenticated (e.g., after COOP popup fallback).
     * Builds a minimal result object from currentUser and runs processOAuthResult.
     */
    public async processCurrentUser(oauthProvider: string): Promise<void> {
        const currentUser = firebase.auth().currentUser;
        console.log('[AUTH] processCurrentUser called, currentUser:', currentUser?.uid ?? 'null');
        if (!currentUser) {
            throw new Error('No current user to process');
        }
        // Build a minimal result object matching what processOAuthResult expects
        const result = {
            user: currentUser,
            credential: null,
        };
        // Skip navigation — caller handles where to go after processing
        await this.processOAuthResult(result, oauthProvider, true);
        console.log('[AUTH] processCurrentUser completed');
    }

    /**
     * Shared post-OAuth processing for both popup and redirect flows.
     */
    private async processOAuthResult(result: any, oauthProvider: string, skipNavigation = false): Promise<void> {
        console.log('[AUTH] processOAuthResult called, skipNavigation:', skipNavigation, 'provider:', oauthProvider);
        const idTokenResult: any = await firebase
            .auth()
            .currentUser?.getIdTokenResult()
            .catch((error) => {
                console.error(error);
            });

        console.log(
            '[AUTH] idTokenResult claims - admin:',
            idTokenResult?.claims?.admin,
            'reg:',
            idTokenResult?.claims?.reg
        );

        if (idTokenResult.claims.admin) {
            this.isAdmin = true;
        } else {
            this.isAdmin = false;
        }
        if (idTokenResult.claims.reg) {
            this.registration = true;
        } else {
            this.registration = false;
        }

        if (!skipNavigation) {
            console.log('[AUTH] navigating to /logging-in');
            this.router.navigate(['logging-in']);
        }

        if (result.credential) {
            // This gives you a Google / Microsoft Access Token.
        }
        if (result.user) {
            const displayNames: Array<string> = result.user.displayName
                ? result.user.displayName.split(' ')
                : undefined;
            const firstNames = displayNames
                ? displayNames.slice(0, displayNames.length > 2 ? displayNames.length - 1 : 1).join(' ')
                : undefined;
            const lastName = displayNames ? displayNames.slice(displayNames.length - 1, 2)[0] : undefined;

            // this.isLoggedIn = true;
            await this.updateUserData1(result.user, oauthProvider, firstNames ?? '', lastName ?? '');
            console.log('[AUTH] updateUserData1 completed');
            this.oauthedUser = result.user;
            if (!this.registration) {
                console.log('[AUTH] calling subscribeToFirebaseUserProfile()');
                this.subscribeToFirebaseUserProfile();
            } else {
                console.log('[AUTH] registration already done, skipping subscribeToFirebaseUserProfile');
            }
        }
    }

    async signInRegular(email: string, password: string) {
        // signing in with firebase using email and password
        // return this.afAuth.signInWithEmailAndPassword(email, password).then(async (result) => {

        return new Promise<void>((resolve, rejects) => {
            this.afAuth
                .signInWithEmailAndPassword(email, password)
                .then(async (result) => {
                    const idTokenResult: any = await firebase
                        .auth()
                        .currentUser?.getIdTokenResult()
                        .catch((error) => {
                            console.error(error);
                        });

                    if (idTokenResult.claims.admin) {
                        this.isAdmin = true;
                    } else {
                        this.isAdmin = false;
                    }
                    if (idTokenResult.claims.reg) {
                        this.registration = true;
                    } else {
                        this.registration = false;
                    }

                    if (result.user) {
                        setTimeout(async () => {
                            const displayNames: Array<string> | undefined = result.user.displayName
                                ? result.user.displayName.split(' ')
                                : undefined;
                            const firstNames = displayNames
                                ? displayNames.slice(0, displayNames.length > 2 ? displayNames.length - 1 : 1).join(' ')
                                : undefined;
                            const lastName = displayNames
                                ? displayNames.slice(displayNames.length - 1, 2)[0]
                                : undefined;

                            await this.updateUserData1(result.user, 'firebase', firstNames ?? '', lastName ?? '');
                            this.oauthedUser = result.user;
                            this.subscribeToFirebaseUserProfile();
                            resolve();
                        });
                    }
                })
                .catch((error) => {
                    rejects(error);
                });
        });
    }

    private subscribeToFirebaseUserProfile() {
        this.user$.subscribe(
            async (userDoc) => {
                if (userDoc) {
                    this.user = userDoc;
                    this.oneInFlight = this.oneInFlight + 1;
                    this.setupDone = userDoc.setupDone ? true : false;

                    //these needs to only happen once the first time.
                    if (!userDoc.setupDone && !this.setupDone && this.oneInFlight < 2) {
                        // Firestore latency-compensation can deliver a *partial* cache
                        // doc as the first emission after updateUserData1's merge when
                        // IndexedDB is cold (first login on a new device / incognito).
                        // That partial doc lacks `setupDone` even for an already
                        // onboarded user — running the one-time setup off it would
                        // reset `isDeveloper` and re-trigger registration. Confirm
                        // against the SERVER before treating this as a brand-new user —
                        // and if that read fails, do nothing: running setup on "could not
                        // read" demoted a developer (OE-215).
                        let read: ServerUserRead = { ok: false };
                        try {
                            const snap = await this.afs.doc<User>(`users/${userDoc.uid}`).ref.get({ source: 'server' });
                            read = { ok: true, exists: !!snap.exists, data: snap.data() as Partial<User> | undefined };
                        } catch (err: unknown) {
                            console.warn('[AUTH] could not confirm setupDone from server; will retry', err);
                        }
                        const decision = decideFirstTimeSetup(read);
                        if (decision.action === 'retry') {
                            this.oneInFlight--; // let the next emission check again
                            return;
                        }
                        if (decision.action === 'skip') {
                            this.setupDone = true;
                            return;
                        }

                        this.setupDone = true;
                        await this.updateUserData2(userDoc, decision.existing);
                        this.addPlans();
                        this.updateUserTransactions(userDoc);
                        await this.addLinkEmailAccountParticle(userDoc);
                        try {
                            const idToken = (await firebase.auth().currentUser?.getIdToken()) as string;
                            await this.apiService
                                .setUserClaimsForRegistration(idToken)
                                .toPromise()
                                .then((email) => email)
                                .catch((err) => console.error('Error occurred', err)); // TODO check if no sensitive data is printed out
                            this.registration = true;
                        } catch (error) {
                            this.registration = false;
                        }
                    }
                }
            },
            (ex) => {
                console.error('Error getting user doc', ex);
            }
        );
    }

    signInAnonymous() {
        this.afAuth
            .signInAnonymously()
            .then(() => {
                // ...
            })
            .catch((error) => {
                // Handle Errors here.
            });
    }

    signOut() {
        // Disconnect presence before signing out to mark user offline immediately
        this.presenceService.disconnect();

        if (location.href.includes('logout')) {
            this.analytics.logEvent('logout', {
                event_category: 'Authentication',
                event_label: 'Logout Screen',
            });
            this.isLoggedIn = false;
            this.clearCache()
                .then(() => {
                    this.signOutAndReloadAtLogin();
                })
                .catch(() => {
                    this.signOutAndReloadAtLogin();
                });
        } else {
            this.router.navigate(['logout']);
        }
    }

    private signOutAndReloadAtLogin() {
        this.afAuth.signOut().then(() => {
            setTimeout(() => {
                // Hard navigate, this will reload the page and clear the cache.
                location.href = (location.href + '').replace('logout', 'login');
                // this.router.navigate(['/login']);
            }, 200);
        });
    }

    /**
     * Handle an invalid session — the user's auth account or Firestore document
     * has been deleted server-side while their browser session was still active.
     * Signs out locally and redirects to login with a clean state.
     */
    private handleInvalidSession() {
        if (this._invalidSessionHandled) return; // prevent multiple redirects
        this._invalidSessionHandled = true;

        this.isLoggedIn = false;
        this.userToken = null;
        this.presenceService.disconnect();

        this.afAuth
            .signOut()
            .then(() => {
                // Hard navigate to login to clear all state
                window.location.href = '/login';
            })
            .catch(() => {
                window.location.href = '/login';
            });
    }
    private _invalidSessionHandled = false;

    private async clearCache(): Promise<void> {
        // Clear user-specific localStorage items (e.g., profile photo cache)
        localStorage.removeItem('userPreferences');

        // Flush last visited URL to Firestore before terminating connection
        await this.lastRouteService.clear();

        return this.afs.firestore
            .terminate()
            .then(() => {
                // console.log('Firestore terminated');
                this.afs.firestore
                    .clearPersistence()
                    .then(() => {
                        // console.log('Cleared persistence');
                    })
                    .catch((ex) => {
                        console.error('Error clearing persistence', ex);
                    });
            })
            .catch((ex) => {
                console.error('Error terminating', ex);
            });
    }

    // #endregion --------------Login/Sign-up to firebase ----------------------------

    // #region -------- Create/Update user data/doc -----------------------------------

    private async addLinkEmailAccountParticle(user: any) {
        const newParticle = this.createNewLinkEmailAccountParticle();
        await this.createFirstParticle(newParticle);
    }

    public createNewLinkEmailAccountParticle() {
        const uid = firebase.auth().currentUser?.uid as string;
        const newParticle = new Particle();
        newParticle.toBlankSystem(uid);
        return newParticle;
    }

    async createFirstParticle(data: Particle): Promise<Particle | null> {
        if (data) {
            // eslint-disable-next-line no-async-promise-executor
            return new Promise(async (resolve, reject) => {
                try {
                    const workspaceRef = this.afs.collection('workspace').doc(`${data.uid}`);

                    await workspaceRef.set({
                        id: data.uid,
                        inbasketCount: 0,
                        pendingCount: 0,
                        filedCount: 0,
                    });

                    const particleDocRef = this.afs
                        .collection('workspace')
                        .doc(`${data.uid}`)
                        .collection('particles')
                        .doc();

                    data.attachmentsMeta = data.attachmentsMeta !== undefined ? data.attachmentsMeta : {};
                    data.creationDate = firestoreTimestampNow();
                    data.internalDate = firestoreTimestampNow();
                    data.folders = data.folders ? data.folders : {};
                    data.forwardFromMessageId =
                        data.forwardFromMessageId !== undefined ? data.forwardFromMessageId : '0';
                    data.particleKey = particleDocRef.ref.id;
                    data.temp = false;
                    data.content ??= '';

                    const particle = { ...data };
                    // create straight into firestore before using store

                    particleDocRef.set(particle).then(() => {
                        // ?
                    });

                    resolve(data);
                } catch (exception) {
                    console.error('Error adding document: ', exception);
                    reject(exception);
                }
            });
        }
        return Promise.resolve(null);
    }

    private async updateUserData1(user: any, oauthProvider: string, firstNames: string, lastName: string) {
        // Sets user data to firestore on login - these can change in google so we update them every login
        const userRef: AngularFirestoreDocument<any> = this.afs.doc(`users/${user.uid}`);
        const data: Partial<User> = {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName,
            originalSignInProvider: oauthProvider,
        };
        if (firstNames) data.firstName = firstNames;
        if (lastName) data.lastName = lastName;

        return userRef.set(data, { merge: true });
    }

    private async updateUserData2(user: any, existing: Partial<User> | null) {
        // One-time first-setup write, built SET-IF-ABSENT (see buildFirstTimeSetupData)
        // against the doc the server just returned: if this ever re-runs for an
        // already-onboarded user, it must not reset owned fields — above all it must
        // never downgrade `isDeveloper`.
        const userRef: AngularFirestoreDocument<any> = this.afs.doc(`users/${user.uid}`);
        const data = buildFirstTimeSetupData(
            { uid: user.uid, email: user.email, displayName: user.displayName },
            existing,
            this.oauthedUser?.photoURL ?? undefined
        );

        try {
            await userRef.set(data, { merge: true });
        } catch (error) {
            console.error('error updateUserData2: ', error);
        }
    }

    private updateUserTransactions(user: any) {
        // Sets user data to firestore on login
        const transactionsRef: AngularFirestoreDocument<any> = this.afs.doc(`transactions/${user.uid}`);
        const data = {
            particleCount: 0,
        };
        return (
            transactionsRef.set(data, { merge: true }),
            (error: any) => {
                // Handle error here
                // Show popup with errors or just console.error
                console.error('error updateUserTransactions: ', error);
            }
        );
    }

    private addPlans() {
        // Sets user data to firestore on login
        let plansRef: AngularFirestoreDocument<any> = this.afs.doc(`plans/plan1`);
        let data: Plan = {
            name: 'plan1',
            // features: {
            //     feature1: true,
            //     emails: true,
            //     compose: true,
            // },
            // routes: {
            //     feature1: ['/secret'],
            //     emails: ['/content'],
            //     compose: ['/feature3'],
            // },
        };
        plansRef.set(data, { merge: true });

        plansRef = this.afs.doc(`plans/plan2`);
        data = {
            name: 'plan2',
            // features: {
            //     feature1: true,
            //     emails: false,
            //     compose: false,
            // },
            // routes: {
            //     feature1: ['/secret'],
            //     emails: ['/content'],
            //     compose: ['/feature3'],
            // },
        };
        plansRef.set(data, { merge: true });
    }

    // private addExcludeLabels(user: any) {
    //     const userRef: AngularFirestoreDocument<any> = this.afs.doc(`users/${user.uid}`);
    //     const data: any = {
    //         excludedLabels: ['CATEGORY_UPDATES', 'CATEGORY_PROMOTIONS', 'CATEGORY_SOCIAL', 'CATEGORY_FORUMS'],
    //         includedLabels: ['SAMPLE'],
    //         serverExcludedLabels: [''],
    //     };
    //     return userRef.set(data, { merge: true });
    // }

    // #endregion -------- Create/Update user data/doc -------------------------------------------------

    // #region -------- Linking sign-in providers -----------------------------------

    /** Resolves when linked; rejects with an AuthLinkError (plain message in .message, .cancelled on popup close). */
    public linkGmailSignIn(): Promise<void> {
        const provider = new firebase.auth.GoogleAuthProvider();
        // Force account selection for linking
        provider.setCustomParameters({
            prompt: 'select_account',
        });
        return linkProviderToUser(firebase.auth().currentUser, provider, PROVIDER_LABELS['google.com']);
    }

    /** Resolves when linked; rejects with an AuthLinkError (plain message in .message, .cancelled on popup close). */
    public linkMicrosoftSignIn(): Promise<void> {
        const provider = new firebase.auth.OAuthProvider('microsoft.com');
        // Force account selection for linking
        provider.setCustomParameters({
            prompt: 'select_account',
        });
        return linkProviderToUser(firebase.auth().currentUser, provider, PROVIDER_LABELS['microsoft.com']);
    }

    /** Remove a sign-in provider; refuses to remove the last remaining method. */
    public unlinkSignInProvider(providerId: SignInProviderId): Promise<void> {
        return unlinkProviderFromUser(firebase.auth().currentUser, providerId, PROVIDER_LABELS[providerId]);
    }

    // #endregion ----------------- Linking sign-in providers -----------------------------------

    // #region -------- Role-based Authorization    -----------------------------------

    // public canRead(user: User): boolean {
    //     const allowedRoles = ['admin', 'editor', 'subscriber'];
    //     return this.checkAuthorization(user, allowedRoles);
    // }

    // public canEdit(user: User): boolean {
    //     const allowedRoles = ['admin', 'editor'];
    //     return this.checkAuthorization(user, allowedRoles);
    // }

    // public canDelete(user: User): boolean {
    //     const allowedRoles = ['admin'];
    //     return this.checkAuthorization(user, allowedRoles);
    // }

    // // determines if user has matching role
    // private checkAuthorization(user: User, allowedRoles: string[]): boolean {
    //     if (!user) {
    //         return false;
    //     }
    //     for (const role of allowedRoles) {
    //         if (user.roles && user.roles[role]) {
    //             return true;
    //         }
    //     }
    //     return false;
    // }

    // #endregion ----------------- Role-based Authorization -----------------------------------

    // #region -------- Feature-based Authorization -----------------------------------

    // canAccess(user: User, forFeature: string, route: string): boolean {
    //     const allowed = [forFeature]; // replace with all user features that is true
    //     // const routesAllowed = this.routesAllowed(user, forFeature);
    //     // return this.checkFeatureAuthorization(user, allowed, routesAllowed, route);
    //     return this.checkFeatureAuthorization(user, allowed, [], route);
    // }

    // // DEPRECATED
    // // private routesAllowed(user: User, forFeature: string): string[] {
    // //     if (!user) {
    // //         return [''];
    // //     }
    // //     return user.routes && user.routes[forFeature];
    // // }

    // private checkFeatureAuthorization(
    //     user: User,
    //     allowedFeatures: string[],
    //     routesAllowed: string[],
    //     route: string
    // ): boolean {
    //     if (!user) {
    //         return false;
    //     }
    //     for (const feature of allowedFeatures) {
    //         if (user.features[feature]) {
    //             for (const r of routesAllowed) {
    //                 if (r === route) {
    //                     return true;
    //                 }
    //             }
    //         }
    //     }
    //     return false;
    // }
}

// #endregion ----------- Feature-based Authorization --------------------------------------
